<template>
  <!-- Embedded in the AI assistant's thread (AiChatPanel). Input -- files
       from the 📎 button or drag-and-drop -- comes from the chat composer
       through addFiles; this block only shows and attaches the cards, and
       renders nothing until there is something to show. -->
  <div v-if="hasAnyResult || attachSuccessCount || canRetryTripInfo" class="ride-documents-block">
      <div class="ride-documents-panel__body">

        <!-- Confirmation of the last successful attach. Stays up until a new
             upload starts or the ride changes -- the reset that follows a
             success empties the cards, and without this line that would read
             as if nothing had happened. -->
        <p v-if="attachSuccessCount" class="ride-documents-panel__success-notice">
          {{ attachSuccessCount === 1
            ? t('rideDocumentsAttachSuccessOne')
            : t('rideDocumentsAttachSuccessMany', { count: attachSuccessCount }) }}
        </p>

        <!-- Location lookup progress -->
        <div v-if="outstandingLookups > 0" class="ride-documents-panel__lookup-summary">
          <span class="ride-documents-panel__lookup-spinner"></span>
          {{ t('importDocumentsLookingUpPlaces', { count: outstandingLookups, total: lookupTotal }) }}
        </div>

        <!-- Result cards -->
        <div class="ride-documents-panel__cards">
          <template v-for="(result, index) in documentImportResults">
            <div
              v-if="!result.discarded"
              :key="index"
              class="ride-documents-panel__card"
              :class="'ride-documents-panel__card--' + result.status"
            >
              <!-- Processing -->
              <div v-if="result.status === 'processing'" class="ride-documents-panel__card-processing">
                <div class="ride-documents-panel__spinner"></div>
                <div class="ride-documents-panel__processing-info">
                  <span>{{ t('importDocumentsProcessing') }}</span>
                  <span class="ride-documents-panel__processing-elapsed">{{ elapsedLabel(result.processingStartedAt) }}</span>
                  <small class="ride-documents-panel__processing-hint">{{ t('importDocumentsProcessingHint') }}</small>
                </div>
                <small class="ride-documents-panel__filename">{{ result.file.name }}</small>
              </div>

              <!-- Not an accommodation document -->
              <div v-else-if="result.status === 'not_accommodation'" class="ride-documents-panel__card-error">
                <p>{{ t('importDocumentsNotAccommodation') }}</p>
                <small class="ride-documents-panel__filename">{{ result.file.name }}</small>
                <button class="ride-documents-panel__btn-discard" @click="discard(index)">
                  {{ t('importDocumentsDiscard') }}
                </button>
              </div>

              <!-- Error -->
              <div v-else-if="result.status === 'error'" class="ride-documents-panel__card-error">
                <p>{{ t(result.error || 'importDocumentsError') }}</p>
                <small class="ride-documents-panel__filename">{{ result.file.name }}</small>
                <button class="ride-documents-panel__btn-discard" @click="discard(index)">
                  {{ t('importDocumentsDiscard') }}
                </button>
              </div>

              <!-- Non-accommodation done card (flight, car_rental, attraction, show) -->
              <div v-else-if="result.status === 'done' && result.type !== 'accommodation'" class="ride-documents-panel__card-points">
                <div class="ride-documents-panel__doc-type-badge">{{ docTypeLabel(result.type) }}</div>
                <span v-if="isCardReady(result)" class="ride-documents-panel__ready-chip">{{ t('rideDocumentsCardReady') }}</span>
                <div
                  v-for="(point, pIdx) in result.points"
                  :key="pIdx"
                  class="ride-documents-panel__point"
                >
                  <div class="ride-documents-panel__point-header">
                    <span class="ride-documents-panel__point-label">
                      {{ point.labelKey ? t(point.labelKey) + (point.labelSuffix ? ' · ' + point.labelSuffix : '') : point.label }}
                    </span>
                    <span v-if="point.date" class="ride-documents-panel__point-date">
                      {{ point.date }}{{ point.time ? ' · ' + point.time : '' }}
                    </span>
                    <button class="ride-documents-panel__btn-remove-point" @click="removePoint(index, pIdx)">
                      {{ t('importDocumentsRemovePoint') }}
                    </button>
                  </div>
                  <div v-if="point.searchQuery" class="ride-documents-panel__point-search-query">
                    🔍 {{ point.searchQuery }}
                  </div>
                  <div class="ride-documents-panel__location">
                    <div v-if="!point.place" class="ride-documents-panel__location-search">
                      <p v-if="lookupStatusFor(index + '-' + pIdx)" class="ride-documents-panel__lookup">
                        <span class="ride-documents-panel__lookup-spinner"></span>
                        {{ lookupLabel(index + '-' + pIdx) }}
                      </p>
                      <p v-else class="ride-documents-panel__location-warning">{{ t('importDocumentsPointLocationRequired') }}</p>
                      <gmap-autocomplete
                        :ref="'pointInput-' + index + '-' + pIdx"
                        :select-first-on-enter="true"
                        :placeholder="t('importDocumentsPointLocationPrompt')"
                        :options="{ sessionToken: getPointSessionToken(index, pIdx) }"
                        class="ride-documents-panel__location-input"
                        @place_changed="(place) => onPointPlaceSelected(index, pIdx, place)"
                      />
                    </div>
                    <div v-else class="ride-documents-panel__location-confirmed">
                      <span class="ride-documents-panel__location-check">&#10003;</span>
                      <span class="ride-documents-panel__location-name">{{ point.place.name }}</span>
                      <button class="ride-documents-panel__btn-change-location" @click="clearPointPlace(index, pIdx)">
                        {{ t('importDocumentsChangeLocation') }}
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Preflight blocker: points whose planned date falls outside
                     the trip. The server would silently drop these instead of
                     reporting the problem, so it is caught here before the
                     document is sent. -->
                <div
                  v-if="cardBlockers[index] && cardBlockers[index].kind === 'pointsRange'"
                  class="ride-documents-blocker"
                >
                  <p class="ride-documents-blocker__text">
                    &#9888; {{ t('rideDocumentsPointsOutOfRange', {
                      count: cardBlockers[index].gap.outOfRangeCount,
                    }) }}
                  </p>
                  <p class="ride-documents-blocker__hint">
                    {{ t('rideDocumentsExtendRideHint', {
                      start: cardBlockers[index].gap.newStart,
                      end: cardBlockers[index].gap.newEnd,
                    }) }}
                  </p>
                  <div class="ride-documents-blocker__actions">
                    <button
                      class="ride-documents-panel__btn-edit"
                      @click="extendRide(cardBlockers[index].gap)"
                    >
                      {{ t('rideDocumentsExtendRide') }}
                    </button>
                    <button class="ride-documents-panel__btn-discard" @click="discard(index)">
                      {{ t('rideDocumentsSkipDocument') }}
                    </button>
                  </div>
                </div>

                <div class="ride-documents-panel__card-actions">
                  <!-- Disabled, not hidden, while an attach is in flight:
                       attachOne returns silently on documentImportIsCreating,
                       so a live-looking button would simply do nothing. -->
                  <button
                    v-if="canAttachCard(index, result)"
                    class="ride-documents-panel__btn-attach-one"
                    :disabled="documentImportIsCreating"
                    @click="attachOne(index)"
                  >
                    {{ documentImportIsCreating
                      ? t('rideDocumentsAttaching')
                      : t('rideDocumentsAttachThisDocument') }}
                  </button>
                  <button class="ride-documents-panel__btn-discard" @click="discard(index)">
                    {{ t('importDocumentsDiscard') }}
                  </button>
                </div>
              </div>

              <!-- Accommodation — preview mode -->
              <div v-else-if="result.status === 'done' && result.type === 'accommodation' && !result.editing" class="ride-documents-panel__card-preview">
                <div class="ride-documents-panel__doc-type-badge">{{ docTypeLabel(result.type) }}</div>
                <span v-if="isCardReady(result)" class="ride-documents-panel__ready-chip">{{ t('rideDocumentsCardReady') }}</span>
                <div v-if="result.extracted.confidence === 'low'" class="ride-documents-panel__warning">
                  &#9888; {{ t('importDocumentsConfidenceLow') }}
                </div>
                <h3 class="ride-documents-panel__hotel-name">{{ result.extracted.data.name }}</h3>
                <p v-if="result.extracted.data.address" class="ride-documents-panel__field">
                  {{ result.extracted.data.address }}
                </p>
                <p class="ride-documents-panel__field">
                  {{ t('importDocumentsCheckIn') }}: {{ result.extracted.data.check_in_date }}
                  &nbsp;&rarr;&nbsp;
                  {{ t('importDocumentsCheckOut') }}: {{ result.extracted.data.check_out_date }}
                </p>
                <p
                  v-if="!accommodationHasDates(result)"
                  class="ride-documents-panel__location-warning"
                >
                  &#9888; {{ t('importDocumentsAccommodationDatesMissing') }}
                </p>
                <p v-if="result.extracted.data.booking_confirmation" class="ride-documents-panel__field">
                  {{ result.extracted.data.booking_confirmation }}
                </p>
                <p v-if="result.extracted.data.text" class="ride-documents-panel__card-text">
                  {{ result.extracted.data.text }}
                </p>
                <div
                  v-if="result.extracted.missing_fields && result.extracted.missing_fields.length > 0"
                  class="ride-documents-panel__missing-fields"
                >
                  <span class="ride-documents-panel__missing-label">{{ t('importDocumentsMissingFields') }}</span>
                  <span
                    v-for="field in result.extracted.missing_fields"
                    :key="field"
                    class="ride-documents-panel__missing-badge"
                  >{{ field }}</span>
                </div>

                <!-- Preflight blocker: the server's own guards, run here so the
                     manager gets a fix instead of a dead-end popup. -->
                <div v-if="cardBlockers[index]" class="ride-documents-blocker">

                  <div v-if="cardBlockers[index].kind === 'invalidDates'">
                    <p class="ride-documents-blocker__text">
                      &#9888; {{ t('rideDocumentsInvalidDates') }}
                    </p>
                    <div class="ride-documents-blocker__actions">
                      <button class="ride-documents-panel__btn-edit" @click="startEdit(index)">
                        {{ t('rideDocumentsFixDocumentDates') }}
                      </button>
                      <button class="ride-documents-panel__btn-discard" @click="discard(index)">
                        {{ t('rideDocumentsSkipDocument') }}
                      </button>
                    </div>
                  </div>

                  <!-- The trip's existing stays are still being fetched -- the
                       normal state on the very first open of this panel after
                       a page load, since nothing else on the page is
                       guaranteed to have fetched them yet. Blocked, not
                       silently passed, until it resolves one way or the
                       other. -->
                  <div v-else-if="cardBlockers[index].kind === 'staysChecking'">
                    <p class="ride-documents-blocker__text">
                      {{ t('rideDocumentsCheckingExistingStays') }}
                    </p>
                  </div>

                  <!-- The trip's existing stays could not be read: never
                       fetched, a stale version header, a dropped request, a
                       stale flag left over from a different ride, ... There is
                       no basis to say this card is clear, so it is blocked
                       rather than silently let through. -->
                  <div v-else-if="cardBlockers[index].kind === 'staysUnreadable'">
                    <p class="ride-documents-blocker__text">
                      &#9888; {{ t('rideDocumentsExistingStaysUnreadable') }}
                    </p>
                    <div class="ride-documents-blocker__actions">
                      <button class="ride-documents-panel__btn-edit" @click="retryAccommodationsFetch">
                        {{ t('rideDocumentsRetryStaysCheck') }}
                      </button>
                      <button class="ride-documents-panel__btn-discard" @click="discard(index)">
                        {{ t('rideDocumentsSkipDocument') }}
                      </button>
                    </div>
                  </div>

                  <div v-else-if="cardBlockers[index].kind === 'range'">
                    <p class="ride-documents-blocker__text">
                      &#9888; {{ t('rideDocumentsOutOfRange', { days: cardBlockers[index].gap.daysAdded }) }}
                    </p>
                    <p class="ride-documents-blocker__hint">
                      {{ t('rideDocumentsExtendRideHint', {
                        start: cardBlockers[index].gap.newStart,
                        end: cardBlockers[index].gap.newEnd,
                      }) }}
                    </p>
                    <div class="ride-documents-blocker__actions">
                      <button
                        class="ride-documents-panel__btn-edit"
                        @click="extendRide(cardBlockers[index].gap)"
                      >
                        {{ t('rideDocumentsExtendRide') }}
                      </button>
                      <button class="ride-documents-panel__btn-discard" @click="discard(index)">
                        {{ t('rideDocumentsSkipDocument') }}
                      </button>
                    </div>
                  </div>

                  <div v-else-if="cardBlockers[index].kind === 'doubleBooking'">
                    <p class="ride-documents-blocker__text">
                      &#9888; {{ t('rideDocumentsDoubleBooking', {
                        existing: cardBlockers[index].conflict.name,
                        incoming: result.extracted.data.name,
                      }) }}
                    </p>
                    <p class="ride-documents-blocker__hint">
                      {{ t('rideDocumentsExistingStayDates', {
                        name: cardBlockers[index].conflict.name,
                        checkIn: cardBlockers[index].conflict.check_in_date,
                        checkOut: cardBlockers[index].conflict.check_out_date,
                      }) }}
                    </p>
                    <div class="ride-documents-blocker__date-row">
                      <label>{{ t('importDocumentsCheckIn') }}</label>
                      <input
                        type="date"
                        class="ride-documents-panel__input ride-documents-panel__input--date"
                        :class="{ 'ride-documents-blocker__input--invalid': conflictFieldInvalid(cardBlockers[index].conflict, 'checkIn') }"
                        :value="conflictDateValue(cardBlockers[index].conflict, 'checkIn')"
                        @input="setConflictDate(cardBlockers[index].conflict, 'checkIn', $event.target.value)"
                      />
                    </div>
                    <div class="ride-documents-blocker__date-row">
                      <label>{{ t('importDocumentsCheckOut') }}</label>
                      <input
                        type="date"
                        class="ride-documents-panel__input ride-documents-panel__input--date"
                        :class="{ 'ride-documents-blocker__input--invalid': conflictFieldInvalid(cardBlockers[index].conflict, 'checkOut') }"
                        :value="conflictDateValue(cardBlockers[index].conflict, 'checkOut')"
                        @input="setConflictDate(cardBlockers[index].conflict, 'checkOut', $event.target.value)"
                      />
                    </div>
                    <!-- Why the last attempt failed, said here rather than in a
                         popup that would drop half these codes on the floor. -->
                    <p
                      v-if="conflictErrorFor(cardBlockers[index].conflict)"
                      class="ride-documents-blocker__error"
                    >
                      {{ t(conflictErrorFor(cardBlockers[index].conflict).key,
                            conflictErrorFor(cardBlockers[index].conflict).params) }}
                    </p>
                    <div class="ride-documents-blocker__actions">
                      <button
                        class="ride-documents-panel__btn-edit"
                        :disabled="!!conflictBusy[cardBlockers[index].conflict.id]"
                        @click="saveConflictDates(cardBlockers[index].conflict)"
                      >
                        {{ t('rideDocumentsChangeExistingDates') }}
                      </button>
                      <button
                        class="ride-documents-panel__btn-discard"
                        :disabled="!!conflictBusy[cardBlockers[index].conflict.id]"
                        @click="removeConflict(cardBlockers[index].conflict)"
                      >
                        {{ t('rideDocumentsRemoveExisting') }}
                      </button>
                      <button class="ride-documents-panel__btn-discard" @click="discard(index)">
                        {{ t('rideDocumentsSkipDocument') }}
                      </button>
                    </div>
                  </div>

                </div>

                <div class="ride-documents-panel__card-actions">
                  <!-- See the note on the other per-card attach button: an
                       in-flight attach makes this one a no-op. -->
                  <button
                    v-if="canAttachCard(index, result)"
                    class="ride-documents-panel__btn-attach-one"
                    :disabled="documentImportIsCreating"
                    @click="attachOne(index)"
                  >
                    {{ documentImportIsCreating
                      ? t('rideDocumentsAttaching')
                      : t('rideDocumentsAttachThisDocument') }}
                  </button>
                  <button class="ride-documents-panel__btn-edit" @click="startEdit(index)">
                    {{ t('importDocumentsEdit') }}
                  </button>
                  <button class="ride-documents-panel__btn-discard" @click="discard(index)">
                    {{ t('importDocumentsDiscard') }}
                  </button>
                </div>
                <!-- Location matching (mandatory) -->
                <div class="ride-documents-panel__location">
                  <!-- State A: no place selected -->
                  <div v-if="!result.place" class="ride-documents-panel__location-search">
                    <p v-if="lookupStatusFor(index)" class="ride-documents-panel__lookup">
                      <span class="ride-documents-panel__lookup-spinner"></span>
                      {{ lookupLabel(index) }}
                    </p>
                    <p v-else class="ride-documents-panel__location-warning">{{ t('importDocumentsLocationRequired') }}</p>
                    <gmap-autocomplete
                      :ref="'locationInput-' + index"
                      :select-first-on-enter="true"
                      :placeholder="t('importDocumentsLocationPrompt')"
                      :options="{ sessionToken: locationSessionTokens[index] }"
                      class="ride-documents-panel__location-input"
                      @place_changed="(place) => onPlaceSelected(index, place)"
                    />
                  </div>
                  <!-- State B: place confirmed -->
                  <div v-else class="ride-documents-panel__location-confirmed">
                    <span class="ride-documents-panel__location-check">&#10003;</span>
                    <span class="ride-documents-panel__location-name">{{ t('importDocumentsLocationConfirmed') }}: {{ result.place.name }}</span>
                    <button
                      class="ride-documents-panel__btn-change-location"
                      @click="clearPlace(index)"
                    >
                      {{ t('importDocumentsChangeLocation') }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Accommodation — edit mode -->
              <div v-else-if="result.status === 'done' && result.type === 'accommodation' && result.editing" class="ride-documents-panel__card-edit">
                <input
                  class="ride-documents-panel__input"
                  :value="result.extracted.data.name"
                  :placeholder="t('importDocumentsHotelName')"
                  @input="updateField(index, 'name', $event.target.value)"
                />
                <input
                  class="ride-documents-panel__input"
                  :value="result.extracted.data.address"
                  :placeholder="t('importDocumentsAddress')"
                  @input="updateField(index, 'address', $event.target.value)"
                />
                <div class="ride-documents-panel__date-row">
                  <label>{{ t('importDocumentsCheckIn') }}</label>
                  <input
                    class="ride-documents-panel__input ride-documents-panel__input--date"
                    type="date"
                    :value="result.extracted.data.check_in_date"
                    @input="updateField(index, 'check_in_date', $event.target.value)"
                  />
                </div>
                <div class="ride-documents-panel__date-row">
                  <label>{{ t('importDocumentsCheckOut') }}</label>
                  <input
                    class="ride-documents-panel__input ride-documents-panel__input--date"
                    type="date"
                    :value="result.extracted.data.check_out_date"
                    @input="updateField(index, 'check_out_date', $event.target.value)"
                  />
                </div>
                <input
                  class="ride-documents-panel__input"
                  :value="result.extracted.data.booking_confirmation"
                  :placeholder="t('importDocumentsConfirmation')"
                  @input="updateField(index, 'booking_confirmation', $event.target.value)"
                />
                <textarea
                  class="ride-documents-panel__textarea"
                  :value="result.extracted.data.text"
                  :placeholder="t('importDocumentsNotes')"
                  @input="updateField(index, 'text', $event.target.value)"
                ></textarea>
                <div class="ride-documents-panel__card-actions">
                  <button class="ride-documents-panel__btn-save" @click="saveEdit(index)">
                    {{ t('importDocumentsSave') }}
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Two uploaded vouchers covering the same nights. The attach action
             refuses these outright, so they are named here rather than left to
             a button that looks alive and does nothing. -->
        <div v-if="documentPairConflicts.length > 0" class="ride-documents-blocker ride-documents-blocker--panel">
          <p class="ride-documents-blocker__text">
            &#9888; {{ t('rideDocumentsDocumentsCollide') }}
          </p>
          <ul class="ride-documents-panel__error-list">
            <li
              v-for="(pair, pairIdx) in documentPairConflicts"
              :key="pairIdx"
              class="ride-documents-panel__error-item"
            >
              {{ t('rideDocumentsDocumentPair', { nameA: pair.nameA, nameB: pair.nameB }) }}
            </li>
          </ul>
          <p class="ride-documents-blocker__hint">{{ t('rideDocumentsDocumentsCollideHint') }}</p>
        </div>

        <!-- Partial attach errors -->
        <div
          v-if="documentImportCreationErrors && documentImportCreationErrors.length > 0"
          class="ride-documents-panel__partial-errors"
        >
          <!-- Deliberately not importDocumentsPartialError, which the create
               screen uses and which speaks only of accommodations: this list
               now carries day notes and trip-information sections too. -->
          <p class="ride-documents-panel__partial-errors-title">{{ t('rideDocumentsPartialError') }}</p>
          <p v-if="attachedCount > 0" class="ride-documents-blocker__hint">
            {{ t('rideDocumentsAlreadyAttached', { count: attachedCount }) }}
          </p>
          <ul class="ride-documents-panel__error-list">
            <!-- Keyed by position, not by name: the list now accumulates
                 across attaches, and two documents may well name the same
                 hotel or the same untitled note. -->
            <li
              v-for="(err, errIdx) in documentImportCreationErrors"
              :key="errIdx + '-' + (err.name || '')"
              class="ride-documents-panel__error-item"
            >
              <!-- A day note or a trip-information section, not a place: the
                   manager needs to know which of the two failed, since a note
                   left behind is invisible on the trip itself. -->
              <template v-if="err.kind === 'extraInfo'">
                {{ t('rideDocumentsNoteFailed', {
                  name: err.name || t('rideDocumentsUntitledNote'),
                }) }}
              </template>
              <template v-else>{{ err.name }}: {{ friendlyCreationError(err.error) }}</template>
            </li>
          </ul>
          <!-- The trip-level sections belong to no card, so once every card has
               been attached there is no per-card button and no bulk button left
               to carry them -- this is the only way back for them. The line
               above disappearing is the confirmation. -->
          <button
            v-if="canRetryTripInfo"
            class="ride-documents-panel__btn-edit"
            :disabled="documentImportIsCreating"
            @click="retryTripInfo"
          >
            {{ documentImportIsCreating
              ? t('rideDocumentsAttaching')
              : t('rideDocumentsRetryTripInfo') }}
          </button>
        </div>

        <p class="ride-documents-panel__ai-notice">
          &#9432; {{ t('rideDocumentsAiNotice') }}
        </p>

      </div>

      <!-- Not shown once the panel is empty, nor once a success notice has
           taken its place -- see showPendingNotice. Sits above the attach bar
           even while nothing is "done" yet (still processing), which is
           exactly when a manager is most likely to assume uploading alone was
           enough. -->
      <p v-if="showPendingNotice" class="ride-documents-panel__pending-notice">
        {{ t('rideDocumentsNotYetAdded') }}
      </p>

      <!-- Attach to ride. Once per-card buttons exist, this one is no longer
           all-or-nothing: it sends every card readyIndexes counts as ready,
           and stays usable even while other cards are still blocked. -->
      <div v-if="confirmedCount > 0" class="ride-documents-panel__attach-bar">
        <button
          class="ride-documents-panel__btn-attach"
          :disabled="documentImportIsCreating
            || readyIndexes.length === 0
            || documentPairConflicts.length > 0"
          @click="attach"
        >
          <span v-if="documentImportIsCreating">{{ t('rideDocumentsAttaching') }}</span>
          <span v-else-if="readyIndexes.length === 1">{{ t('rideDocumentsAttachOne') }}</span>
          <span v-else-if="readyIndexes.length > 1">{{ t('rideDocumentsAttachMany', { count: readyIndexes.length }) }}</span>
          <span v-else>{{ t('rideDocumentsAttach') }}</span>
        </button>
        <p v-if="documentPairConflicts.length > 0" class="ride-documents-panel__attach-hint">
          {{ t('rideDocumentsResolveConflictsFirst') }}
        </p>
        <p v-else-if="waitingCount > 0" class="ride-documents-panel__attach-hint">
          {{ waitingCount === 1
            ? t('rideDocumentsWaitingOne')
            : t('rideDocumentsWaitingMany', { count: waitingCount }) }}
        </p>
      </div>

  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { translationMixin } from '@/mixins/translationMixin';
import { resolvePlace } from '@/common/resolvePlace';
import { createPlaceResolveQueue } from '@/utils/placeResolveQueue';
import { hasBothDates, fromBackendDate } from '@/utils/documentImportDates';
import {
  flattenRideStays,
  accommodationsFetchCovers,
  staysCheckReady as computeStaysCheckReady,
  staysCheckLoading as computeStaysCheckLoading,
  accommodationBlocker,
  pointsRangeGap,
  buildStayDateUpdatePayload,
  stayUpdateErrorKey,
  stayDeleteErrorKey,
} from '@/utils/rideDocumentConflicts';
import accommodationApi from '@/api/accommodation';
import { pickDocumentFiles } from '@/utils/chatDocuments';

// Pause between one place lookup and the next. Raise it if Google starts
// answering OVER_QUERY_LIMIT: waiting a little longer between calls is cheaper
// than being throttled, which costs seconds of backoff and can push a place all
// the way to manual entry.
const PLACE_LOOKUP_GAP_MS = 200;

export default {
  name: 'RideDocumentsPanel',
  mixins: [translationMixin],

  props: {
    rideId: { type: Number, required: true },
  },

  data() {
    return {
      locationSessionTokens: [],
      pointSessionTokens: {},
      // Which places are still being looked up, keyed the same way as
      // _autoResolveAttempted. Absent means settled, one way or the other.
      lookupStatus: {},
      lookupTotal: 0,
      now: Date.now(),
      // Dates the manager is typing for an existing stay, keyed by its id. Only
      // what was actually edited lives here; anything absent falls back to the
      // stay as the server has it, so a refetch is never fought by stale input.
      conflictDates: {},
      conflictBusy: {},
      // Why the last repair attempt on a stay failed, keyed by its id:
      // { key, params, field }. Reported on the card, never through a popup.
      conflictErrors: {},
      // How many documents the last successful attach put on the trip. Captured
      // right before documentImportAttachToRide runs, since a full success
      // clears documentImportResults via documentImportReset -- by the time the
      // panel could read a count back off the store it would already be zero.
      // Stays set (and the confirmation stays visible) until a new upload
      // starts or the ride changes.
      attachSuccessCount: null,
    };
  },

  computed: {
    documentImportGeneration() {
      return this.$store.state.documentImport.generation;
    },
    ...mapGetters([
      'documentImportResults',
      'documentImportIsProcessing',
      'documentImportProcessingCount',
      'documentImportAllDoneHavePlace',
      'documentImportAccommodationsMissingDates',
      'documentImportIsCreating',
      'documentImportCreationErrors',
      'documentImportDoubleBookingConflicts',
      'documentImportGeneralInfo',
      'documentImportResultsRideId',
      'allAccommodations',
      'accommodationsStatus',
      'accommodationsRideId',
      'allRide',
      'apptoken',
    ]),
    // The ride the panel is attaching to, as the ride module keeps it
    // (rootState.ride.ride.ride), serialized DD-MM-YYYY by the backend while
    // every helper here compares ISO.
    rideStartIso() {
      return fromBackendDate(this.allRide && this.allRide.ride && this.allRide.ride.start_date);
    },
    rideEndIso() {
      return fromBackendDate(this.allRide && this.allRide.ride && this.allRide.ride.end_date);
    },
    // A computed read of the getter, never a cached copy: after a fix is saved
    // and fetchAccommodations lands, every blocker re-evaluates by itself.
    rideStays() {
      return flattenRideStays(this.allAccommodations);
    },
    // Whether this.allAccommodations can actually be trusted for THIS ride's
    // double-booking check right now. See staysCheckReady's own comment
    // (rideDocumentConflicts.js) for the full reasoning: a never-fetched ride
    // and a genuinely stay-free one are byte-identical ({}), so the value
    // alone can never answer this.
    staysCheckReady() {
      return computeStaysCheckReady({
        rideId: this.rideId,
        accommodationsRideId: this.accommodationsRideId,
        accommodationsStatus: this.accommodationsStatus,
        allAccommodations: this.allAccommodations,
      });
    },
    // A fetch for THIS ride's stays is currently in flight -- worth saying so
    // rather than showing the same "could not check" wording a real failure
    // gets.
    staysCheckLoading() {
      return computeStaysCheckLoading(this.rideId, this.accommodationsRideId, this.accommodationsStatus);
    },
    cardBlockers() {
      const blockers = {};
      this.documentImportResults.forEach((result, index) => {
        blockers[index] = this.cardBlocker(result);
      });
      return blockers;
    },
    // Two uploaded vouchers whose nights overlap each other. The attach action
    // returns early and silently on these (documentImport.js:446), so without
    // showing them the button would look alive and do nothing -- for the bulk
    // button and for every per-card button alike, since the guard is global.
    documentPairConflicts() {
      return this.documentImportDoubleBookingConflicts || [];
    },
    // Every card an attach (bulk or per-card) can actually send right now:
    // parsed, kept, its place(s) resolved, no blocker of its own, and no
    // staged voucher colliding with another staged voucher. This is what the
    // bulk button now attaches -- only these -- instead of refusing outright
    // the moment any other card has a problem.
    readyIndexes() {
      const indexes = [];
      this.documentImportResults.forEach((result, index) => {
        if (this.canAttachCard(index, result)) indexes.push(index);
      });
      return indexes;
    },
    // Cards that are done and still staged but are NOT part of readyIndexes --
    // missing a place, carrying a blocker, or held up by a staged-vs-staged
    // conflict. What the bulk button's "N waiting" hint counts.
    waitingCount() {
      return this.documentImportResults.filter((result, index) => result
        && result.status === 'done' && !result.discarded && !this.canAttachCard(index, result)).length;
    },
    // A trip-level section was rejected and is still staged. It is attached by
    // whichever run happens to go first, so while any card remains it rides
    // along with the next attach; once none do, this is its only way onto the
    // trip.
    canRetryTripInfo() {
      return (this.documentImportGeneralInfo || []).length > 0
        && (this.documentImportCreationErrors || []).some(err => err.resultIndex === null);
    },
    hasAnyResult() {
      return this.documentImportResults.length > 0;
    },
    activeCount() {
      return this.documentImportResults.filter(r => !r.discarded).length;
    },
    confirmedCount() {
      return this.documentImportResults.filter(r => r.status === 'done' && !r.discarded).length;
    },
    outstandingLookups() {
      return Object.keys(this.lookupStatus).length;
    },
    // Cards a partially failed attach already put on the trip. They are marked
    // discarded so a retry cannot post them twice, which also removes them from
    // the list -- this is what accounts for them.
    attachedCount() {
      return this.documentImportResults.filter(r => r && r.attached).length;
    },
    // Something is staged but not yet on the trip: any card still in the list
    // (processing, done, even a still-fixable error) that has not been
    // discarded. Cleared the instant activeCount drops back to zero, which a
    // successful attach already does by resetting the results -- and hidden
    // for the moment right after that reset while the success line is up, so
    // the two notices never show together.
    showPendingNotice() {
      return this.activeCount > 0 && !this.attachSuccessCount;
    },
    // What the assistant's closed button shows as its badge: documents still
    // being read, else what a press of Attach would actually put on the trip
    // (readyIndexes -- a parsed card that is blocked is not going anywhere,
    // and counting it promised more documents than the panel then offered).
    // The block stays mounted while the panel is closed so this keeps moving.
    documentsStatus() {
      return { processing: this.documentImportProcessingCount, ready: this.readyIndexes.length };
    },
  },

  watch: {
    // Declared before documentImportResults so it runs first when a reset and
    // a new upload land in the same tick. The store numbers cards from 0 again
    // after every reset (a full attach, a ride switch), so what this panel
    // remembers per card index belongs to cards that are gone; kept, it marked
    // the next document's card 0 as already looked up and Google was never asked.
    documentImportGeneration() {
      this._forgetLookups();
    },
    documentImportResults: {
      deep: true,
      handler(results) {
        // Everything goes through one shared queue, which runs lookups serially
        // and lets Google set the pace. A second document arriving mid-flight
        // simply appends to it; previously each watcher pass restarted its own
        // stagger from zero and the batches collided.
        results.forEach((result, index) => {
          if (result.status !== 'done') return;
          if (result.type === 'accommodation') {
            if (!result.place && !this._autoResolveAttempted[index]) {
              this._autoResolveAttempted[index] = true;
              this._startLookup(index);
              this.autoResolvePlace(index, result);
            }
          } else if (result.points) {
            result.points.forEach((point, pIdx) => {
              const key = `${index}-${pIdx}`;
              if (!point.place && !this._autoResolveAttempted[key]) {
                this._autoResolveAttempted[key] = true;
                this._startLookup(key);
                this.autoResolvePointPlace(index, pIdx, point);
              }
            });
          }
        });
        this.$gmapApiPromiseLazy().then(() => {
          while (this.locationSessionTokens.length < results.length) {
            this.locationSessionTokens.push(new google.maps.places.AutocompleteSessionToken()); // eslint-disable-line no-undef
          }
        });
      },
    },
    // Nothing else on the page is guaranteed to have fetched this ride's
    // stays yet (SingleRideChecklist does, on its own mount, but it is not
    // guaranteed to be on the page), and the double-booking check needs them.
    // Fetched when the first document arrives, not in mounted(), so a manager
    // who never uploads one never pays for the extra request.
    hasAnyResult(has) {
      if (has) this.ensureAccommodationsFetched();
    },
    documentsStatus: {
      immediate: true,
      handler(status) {
        this.$emit('status', status);
      },
    },
    // The assistant is reused when the manager moves from ride A to ride B
    // (the component is not destroyed), so staged cards would otherwise sit
    // there and attach to ride B. Everything staged belongs to ride A: drop it.
    rideId(newId) {
      this.startFreshForRide(newId);
    },
  },

  created() {
    this._autoResolveAttempted = {};
    this._throttleRounds = {};
    this._throttleTimers = [];
    this._placeQueue = createPlaceResolveQueue({ minGapMs: PLACE_LOOKUP_GAP_MS });
    // Bumped on every ride change; a place lookup that started before it
    // writes nothing (see _lookupIsStale).
    this._lookupGeneration = 0;
  },

  mounted() {
    // The store outlives this component: cards staged on another ride (the
    // manager went A -> my rides -> C) or by the create-a-ride import page are
    // not this ride's, and attaching them here would put them on the wrong trip.
    if (this.documentImportResults.length && this.documentImportResultsRideId !== this.rideId) {
      this.documentImportReset();
    }
    this.documentImportSetTargetRideId(this.rideId);

    this._clockTimer = setInterval(() => { this.now = Date.now(); }, 1000);
    this.$gmapApiPromiseLazy().then(() => {
      this.locationSessionTokens = this.documentImportResults.map(
        () => new google.maps.places.AutocompleteSessionToken() // eslint-disable-line no-undef
      );
    });
  },

  beforeDestroy() {
    this._leaving = true;
    (this._throttleTimers || []).forEach(clearTimeout);
    clearInterval(this._clockTimer);
    // The store outlives this component and nothing else ever clears this.
    // Leaving it set makes the OTHER flow behave as if it were attaching to a
    // ride: /import-ride-documents suppresses its AI ride-name suggestion when
    // a target ride is present (documentImport.js:284), and without a name its
    // Create button never enables. Both actions that need the target set it
    // themselves on the way in, so dropping it here costs this panel nothing.
    this.documentImportSetTargetRideId(null);
  },

  methods: {
    ...mapMutations([
      'documentImportSetTargetRideId',
      'documentImportSetPlace',
      'documentImportSetPointPlace',
      'documentImportDiscardResult',
      'documentImportRemovePoint',
      'documentImportUpdateField',
      'documentImportSetEditing',
      'documentImportReset',
    ]),
    ...mapActions([
      'documentImportUploadFile',
      'documentImportAttachToRide',
      'documentImportExtendRideDates',
      'fetchAccommodations',
      'fetchRide',
      'showModalWithParameters',
    ]),

    // Makes sure a fetch for THIS ride's stays is running or has already
    // landed, without ever firing a redundant one. Skipped when the store
    // already holds a 'ready' or in-flight 'loading' result for this exact
    // rideId; run (and its rejection swallowed -- staysCheckReady/'error'
    // already reflects a failure, there is nothing else to do with it here)
    // for every other state, including a stale id or status left over from a
    // different ride, which must never be read as "verified" for this one.
    ensureAccommodationsFetched() {
      if (accommodationsFetchCovers(this.rideId, this.accommodationsRideId, this.accommodationsStatus)) {
        return;
      }
      this.fetchAccommodations().catch(() => {});
    },

    // The manual "try again" on a staysUnreadable card. Always dispatches --
    // unlike ensureAccommodationsFetched it does not skip a same-ride
    // 'loading', since a manager pressing this after a failure wants a fresh
    // attempt, not to be told one is already running.
    retryAccommodationsFetch() {
      this.fetchAccommodations().catch(() => {});
    },

    // Preflight for one card, in the server's own order. Only a parsed
    // accommodation can collide; a card still missing its dates is already
    // reported by documentImportAccommodationsMissingDates and must not be
    // shouted at twice.
    //
    // A non-accommodation card (flight, car rental, attraction, show,
    // itinerary) has no server-side range check to mirror -- the server
    // silently drops it instead (see the file header of rideDocumentConflicts
    // for why) -- so its points are preflighted here with the same
    // resolvePointDate the actual attach uses, and reported the same way an
    // accommodation range problem is: a 'range' blocker carrying a gap the
    // existing "extend the trip" button already knows how to act on.
    cardBlocker(result) {
      if (!result || result.discarded) return null;
      if (result.status !== 'done') return null;
      if (result.type === 'accommodation') {
        const data = (result.extracted && result.extracted.data) || {};
        if (!hasBothDates(data.check_in_date, data.check_out_date)) return null;
        // The double-booking check below is only as good as this.rideStays --
        // and a never-fetched ride, a mid-fetch ride, a stale id left over
        // from a different ride, a failed fetch, or a response shaped like a
        // version error (staysAreReadable) would all flatten to the SAME "no
        // stays" list flattenRideStays would silently wave every card
        // through on. staysCheckReady is the one place that tells "verified"
        // apart from all of those. Refuse instead of guessing.
        if (!this.staysCheckReady) {
          return { kind: this.staysCheckLoading ? 'staysChecking' : 'staysUnreadable' };
        }
        return accommodationBlocker({
          checkIn: data.check_in_date,
          checkOut: data.check_out_date,
          rideStart: this.rideStartIso,
          rideEnd: this.rideEndIso,
          stays: this.rideStays,
        });
      }
      const gap = pointsRangeGap(this.rideStartIso, this.rideEndIso, result.points);
      return gap ? { kind: 'pointsRange', gap } : null;
    },

    // Never automatic: extending moves every day the ride renders and the server
    // books it as a billing adjustment.
    async extendRide(gap) {
      // Same guard as attach(): a successful attach runs documentImportReset,
      // which nulls targetRideId, and documentImportExtendRideDates reads the
      // ride to extend straight out of it. Without this the button would post
      // to /update_ride_duration_api/null for the rest of the visit.
      this.documentImportSetTargetRideId(this.rideId);
      const ok = await this.documentImportExtendRideDates({
        newStart: gap.newStart, newEnd: gap.newEnd,
      });
      if (!ok) return;
      // documentImportExtendRideDates already refetched the ride; the stays are
      // ours to refresh, and both feed the blockers.
      await Promise.all([this.fetchRide(), this.fetchAccommodations()]);
    },

    conflictDateValue(stay, field) {
      const edited = this.conflictDates[stay.id];
      if (edited && edited[field]) return edited[field];
      return field === 'checkIn' ? stay.check_in_date : stay.check_out_date;
    },

    setConflictDate(stay, field, value) {
      const current = this.conflictDates[stay.id] || {
        checkIn: stay.check_in_date,
        checkOut: stay.check_out_date,
      };
      this.$set(this.conflictDates, stay.id, Object.assign({}, current, { [field]: value }));
      // The complaint was about what was typed before; typing again retires it.
      this.$delete(this.conflictErrors, stay.id);
    },

    // Everything that can go wrong with a repair is reported on the card that
    // asked for it. Nothing here goes to showModalWithParameters: popups.js:716
    // renders a code only when it is a key of errorNumberConverter and silently
    // drops everything else, and neither "fail - date are not at ride range"
    // (views.py:291) nor "general_error" is one -- so a popup here would be a
    // button press that visibly does nothing.
    setConflictError(stayId, key, params, field) {
      this.$set(this.conflictErrors, stayId, {
        key, params: params || {}, field: field || '',
      });
    },

    conflictErrorFor(stay) {
      return this.conflictErrors[stay.id] || null;
    },

    conflictFieldInvalid(stay, field) {
      const error = this.conflictErrors[stay.id];
      return !!(error && error.field === field);
    },

    // Only the out-of-range message takes parameters, and they are the trip's
    // own dates -- the manager needs the window, not just the verdict.
    conflictErrorParams(key) {
      if (key === 'rideDocumentsExistingOutOfRange') {
        return { start: this.rideStartIso, end: this.rideEndIso };
      }
      return {};
    },

    // Shortening an existing stay can leave a night without a hotel. That is the
    // manager's call, so nothing here blocks it.
    async saveConflictDates(stay) {
      if (this.conflictBusy[stay.id]) return;
      const checkIn = this.conflictDateValue(stay, 'checkIn');
      const checkOut = this.conflictDateValue(stay, 'checkOut');

      // Both of these are visible without asking the server, and both name the
      // field at fault -- "the dates don't match the trip" would be the wrong
      // sentence for a check-out that simply precedes its check-in.
      if (!hasBothDates(checkIn, checkOut)) {
        this.setConflictError(
          stay.id, 'rideDocumentsExistingDatesMissing', {}, checkIn ? 'checkOut' : 'checkIn',
        );
        return;
      }
      if (checkIn >= checkOut) {
        this.setConflictError(stay.id, 'rideDocumentsExistingDatesReversed', {}, 'checkOut');
        return;
      }

      this.$set(this.conflictBusy, stay.id, true);
      try {
        const response = await accommodationApi.updateAccommodation(
          this.apptoken, stay.id, buildStayDateUpdatePayload(stay, checkIn, checkOut),
        );
        // The server re-runs range and overlap here too, excluding the row being
        // edited -- so a manager who drags the existing stay out of the trip to
        // clear the overlap lands here, and has to be told why.
        const code = response && response.data && response.data.ServerMassageCode;
        if (code !== 'success') {
          const key = stayUpdateErrorKey(code);
          this.setConflictError(stay.id, key, this.conflictErrorParams(key), '');
          return;
        }
        this.$delete(this.conflictDates, stay.id);
        this.$delete(this.conflictErrors, stay.id);
        await this.fetchAccommodations();
      } catch {
        this.setConflictError(stay.id, 'rideDocumentsExistingSaveFailed', {}, '');
      } finally {
        this.$delete(this.conflictBusy, stay.id);
      }
    },

    // Deleting a booked stay also destroys its confirmation number and vouchers,
    // so it is confirmed first -- the same modal AiChatPanel opens before the
    // same act (AiChatPanel.vue:729-747).
    removeConflict(stay) {
      if (this.conflictBusy[stay.id]) return;
      this.showModalWithParameters({
        type: 'confirmRemoveAiAccommodation',
        inerModalParameters: {
          name: stay.name,
          onConfirm: () => this.removeConflictConfirmed(stay),
        },
      });
    },

    async removeConflictConfirmed(stay) {
      if (this.conflictBusy[stay.id]) return;
      this.$set(this.conflictBusy, stay.id, true);
      try {
        const response = await accommodationApi.deleteAccommodation(this.apptoken, stay.id);
        // deleteAccommodationApi answers "success", "user_is_not_authorized" or
        // "fail_to_update_accommodation" (accommodation/api.py:16-25). Taking
        // any of them for success would leave the blocker standing with no word
        // on why.
        const code = response && response.data && response.data.ServerMassageCode;
        if (code !== 'success') {
          this.setConflictError(stay.id, stayDeleteErrorKey(code), {}, '');
          return;
        }
        this.$delete(this.conflictDates, stay.id);
        this.$delete(this.conflictErrors, stay.id);
        await this.fetchAccommodations();
      } catch {
        this.setConflictError(stay.id, 'rideDocumentsExistingRemoveFailed', {}, '');
      } finally {
        this.$delete(this.conflictBusy, stay.id);
      }
    },

    // The bug this guards against: a successful attach runs
    // documentImportReset, which clears targetRideId back to null. Setting it
    // only once in mounted() would make every attach after the first a no-op.
    //
    // Sends every card readyIndexes counts as ready -- not necessarily every
    // staged card. A card still blocked, or one the store still marks
    // "processing", is left in the panel for the manager to resolve; only
    // once nothing remains staged does the store reset the panel to empty.
    async attach() {
      if (this.documentImportIsCreating || this.readyIndexes.length === 0
        || this.documentPairConflicts.length > 0) return;
      // Captured before the action runs: a full success can commit
      // documentImportReset, which empties documentImportResults, so
      // readyIndexes would already read 0 by the time this resolves.
      const readyCount = this.readyIndexes.length;
      const sent = this.snapshotResults(this.readyIndexes);
      this.documentImportSetTargetRideId(this.rideId);
      const attached = await this.documentImportAttachToRide({
        t: this.t, resultIndexes: this.readyIndexes,
      });
      if (!attached) return;
      this.afterSuccessfulAttach(readyCount);
      this.$emit('attached', sent);
    },

    // The per-card "add this document" button: the same action, scoped to one
    // result. A card with a blocker of its own, or one not yet ready, never
    // reaches here -- the button is not rendered for it (canAttachCard).
    async attachOne(index) {
      if (this.documentImportIsCreating) return;
      const result = this.documentImportResults[index];
      if (!this.canAttachCard(index, result)) return;
      const sent = this.snapshotResults([index]);
      this.documentImportSetTargetRideId(this.rideId);
      const attached = await this.documentImportAttachToRide({
        t: this.t, resultIndexes: [index],
      });
      if (!attached) return;
      this.afterSuccessfulAttach(1);
      this.$emit('attached', sent);
    },

    // Copies of the cards about to be attached, taken before the action runs
    // for the same reason readyCount is: a full success resets the store and
    // the cards are gone by the time the assistant is told what was added.
    snapshotResults(indexes) {
      return indexes.map(i => ({ ...this.documentImportResults[i] }));
    },

    // Re-posts only the trip-level sections a previous run lost. An empty
    // resultIndexes selects no card at all, and the general-information
    // operations are the ones filterAttachOperationsByResult never filters
    // out -- so this run carries exactly what is still owed.
    async retryTripInfo() {
      if (this.documentImportIsCreating) return;
      this.documentImportSetTargetRideId(this.rideId);
      await this.documentImportAttachToRide({ t: this.t, resultIndexes: [] });
    },

    // Shared tail of a successful attach, bulk or per-card.
    afterSuccessfulAttach(count) {
      // These are keyed by the id of an EXISTING stay and only ever cleared
      // once nothing is left staged (documentImportResults empty means the
      // store reset the whole panel) -- a card still blocked elsewhere in the
      // panel may still need the edit the manager is mid-typing.
      if (this.documentImportResults.length === 0) {
        this.conflictDates = {};
        this.conflictErrors = {};
        this.conflictBusy = {};
      }
      this.attachSuccessCount = count;
    },

    // Called by the assistant's composer (📎, drag-and-drop). Uploads what
    // it can and reports the rest by name, so the chat can say why.
    addFiles(files) {
      const { accepted, rejected, overLimit } = pickDocumentFiles(files, this.activeCount);
      // A successful attach resets the store, target included; set it again so
      // the new cards are stamped as this ride's.
      if (accepted.length) {
        this.attachSuccessCount = null;
        this.documentImportSetTargetRideId(this.rideId);
      }
      accepted.forEach(f => this.documentImportUploadFile(f));
      return { accepted: accepted.map(f => f.name), rejected, overLimit };
    },

    // The success line stays until the manager moves on; the chat calls this
    // when they send their next message, so it does not sit under every reply.
    dismissAttachSuccess() {
      this.attachSuccessCount = null;
    },

    // Everything staged belongs to the previous ride: drop it, along with every
    // place lookup still queued or waiting on a throttle retry for it.
    startFreshForRide(rideId) {
      this.documentImportReset();
      this.documentImportSetTargetRideId(rideId);
      this.attachSuccessCount = null;
      this.conflictDates = {};
      this.conflictErrors = {};
      this.conflictBusy = {};
      this._forgetLookups();
    },

    // Drops every per-card-index record of place lookups, and the lookups
    // themselves, queued or waiting on a throttle retry.
    _forgetLookups() {
      this.lookupStatus = {};
      this.lookupTotal = 0;
      this.locationSessionTokens = [];
      this.pointSessionTokens = {};
      this._autoResolveAttempted = {};
      this._throttleRounds = {};
      this._throttleTimers.forEach(clearTimeout);
      this._throttleTimers = [];
      this._placeQueue = createPlaceResolveQueue({ minGapMs: PLACE_LOOKUP_GAP_MS });
      this._lookupGeneration += 1;
    },

    _lookupIsStale(generation) {
      return this._leaving || generation !== this._lookupGeneration;
    },

    // Parsed, kept, every place confirmed. The one place this rule lives now:
    // the ready chip, readyIndexes, the bulk button's count and the closed
    // panel's badge all come through here, so they cannot drift apart.
    isCardReady(result) {
      if (!result || result.discarded || result.status !== 'done') return false;
      if (result.type === 'accommodation') return result.place !== null;
      return (result.points || []).length > 0 && (result.points || []).every(p => p.place !== null);
    },

    // Whether a card can actually be sent by an attach right now, alone or as
    // part of the bulk run: ready (isCardReady) AND free of a blocker of its
    // own AND no staged voucher colliding with another one -- the same global
    // guard documentImportAttachToRide applies regardless of scope, so a
    // per-card button must respect it too or it would look alive and do
    // nothing when pressed.
    canAttachCard(index, result) {
      return this.isCardReady(result)
        && !this.cardBlockers[index]
        && this.documentPairConflicts.length === 0;
    },

    elapsedLabel(startedAt) {
      if (!startedAt) return '';
      const secs = Math.floor((this.now - startedAt) / 1000);
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
    },

    startEdit(index) {
      this.documentImportSetEditing({ index, editing: true });
    },

    saveEdit(index) {
      this.documentImportSetEditing({ index, editing: false });
    },

    discard(index) {
      // The action, not the mutation: it also deletes the uploaded document,
      // which nothing references once the card is gone.
      this.$store.dispatch('documentImportDiscardResultAndCleanup', index);
    },

    removePoint(resultIndex, pointIndex) {
      this.documentImportRemovePoint({ resultIndex, pointIndex });
    },

    updateField(index, field, value) {
      this.documentImportUpdateField({ index, field, value });
    },

    onPlaceSelected(index, place) {
      this._endLookup(index);
      this.documentImportSetPlace({ index, place });
      this.$gmapApiPromiseLazy().then(() => {
        this.$set(this.locationSessionTokens, index, new google.maps.places.AutocompleteSessionToken()); // eslint-disable-line no-undef
      });
    },

    async autoResolvePlace(index, result) {
      const data = result.extracted && result.extracted.data;
      if (!data) return;
      const query = [data.name, data.city, data.country].filter(Boolean).join(' ');
      if (!query) return;

      const generation = this._lookupGeneration;
      const sessionToken = this.locationSessionTokens[index];
      const { place, status } = await this._placeQueue.run(() => resolvePlace(
        this.$gmapApiPromiseLazy.bind(this), query,
        { fields: ['place_id', 'name', 'geometry', 'address_components'], sessionToken },
      ));
      if (this._lookupIsStale(generation)) return;

      if (!place) {
        if (status === 'OVER_QUERY_LIMIT') {
          this._markLookupWaiting(index);
          this._scheduleThrottleRetry(index, () => this.autoResolvePlace(index, result));
        } else {
          this._endLookup(index);
        }
        this.preseedLocationInput(index, query);
        return;
      }

      this._endLookup(index);
      if (this._resultAlreadyPlaced(index)) return;
      await this.$gmapApiPromiseLazy(); // eslint-disable-line no-undef
      if (this._lookupIsStale(generation)) return;
      this.$set(this.locationSessionTokens, index, new google.maps.places.AutocompleteSessionToken()); // eslint-disable-line no-undef
      this.documentImportSetPlace({ index, place });
    },

    preseedLocationInput(index, query) {
      this.$nextTick(() => {
        const ref = this.$refs['locationInput-' + index];
        if (!ref) return;
        const input = ref.$el ? ref.$el.querySelector('input') : null;
        if (!input || input.value) return;
        input.value = query;
      });
    },

    clearPlace(index) {
      this.documentImportSetPlace({ index, place: null });
    },

    getPointSessionToken(resultIndex, pointIndex) {
      return this.pointSessionTokens[`${resultIndex}-${pointIndex}`];
    },

    onPointPlaceSelected(resultIndex, pointIndex, place) {
      this._endLookup(`${resultIndex}-${pointIndex}`);
      this.documentImportSetPointPlace({ resultIndex, pointIndex, place });
      this.$gmapApiPromiseLazy().then(() => {
        this.$set(this.pointSessionTokens, `${resultIndex}-${pointIndex}`, new google.maps.places.AutocompleteSessionToken()); // eslint-disable-line no-undef
      });
    },

    clearPointPlace(resultIndex, pointIndex) {
      this.documentImportSetPointPlace({ resultIndex, pointIndex, place: null });
    },

    // A lookup is outstanding until it settles; the card and the summary line
    // both read this.
    _startLookup(key) {
      this.lookupTotal += 1;
      this.$set(this.lookupStatus, key, 'searching');
    },

    _markLookupWaiting(key) {
      if (this.lookupStatus[key]) this.$set(this.lookupStatus, key, 'waiting');
    },

    _endLookup(key) {
      this.$delete(this.lookupStatus, key);
    },

    accommodationHasDates(result) {
      const data = (result.extracted && result.extracted.data) || {};
      return hasBothDates(data.check_in_date, data.check_out_date);
    },

    lookupStatusFor(key) {
      return this.lookupStatus[key] || '';
    },

    lookupLabel(key) {
      const status = this.lookupStatus[key];
      if (status === 'waiting') return this.t('importDocumentsWaitingForGoogle');
      if (status === 'searching') return this.t('importDocumentsSearchingLocation');
      return '';
    },

    // The search box stays usable while we look, so the traveller may well have
    // picked the place already. Never overwrite that with a late result.
    _pointAlreadyPlaced(resultIndex, pointIndex) {
      const result = this.documentImportResults[resultIndex];
      const point = result && result.points && result.points[pointIndex];
      return !!(point && point.place);
    },

    _resultAlreadyPlaced(index) {
      const result = this.documentImportResults[index];
      return !!(result && result.place);
    },

    // The queue already retried this lookup with backoff and Google is still
    // refusing. Come back to it after a longer pause rather than leaving the
    // place marked missing — but bounded, so an exhausted daily quota cannot
    // turn into an endless loop.
    _scheduleThrottleRetry(key, retry) {
      const rounds = (this._throttleRounds[key] || 0) + 1;
      this._throttleRounds[key] = rounds;

      // Out of patience: leave it preseeded for the traveller to confirm by hand.
      if (rounds > 2) {
        this._endLookup(key);
        return;
      }

      // The flag stays set throughout: this timer is the retry, so leaving the
      // key "unattempted" would let the results watcher start a second,
      // concurrent lookup for the same place while we wait.
      const waitMs = 15000 * rounds;
      this._throttleTimers.push(setTimeout(() => {
        if (this._leaving) return;
        retry();
      }, waitMs));
    },

    autoResolvePointPlace(resultIndex, pointIndex, point) {
      this._tryResolvePointQuery(resultIndex, pointIndex, point.searchQuery, point.searchQueryFallback || '');
    },

    async _tryResolvePointQuery(resultIndex, pointIndex, query, fallback) {
      if (!query) return;

      const key = `${resultIndex}-${pointIndex}`;
      const generation = this._lookupGeneration;
      await this.$gmapApiPromiseLazy();
      if (this._lookupIsStale(generation)) return;
      if (!this.pointSessionTokens[key]) {
        this.$set(this.pointSessionTokens, key, new google.maps.places.AutocompleteSessionToken()); // eslint-disable-line no-undef
      }
      const sessionToken = this.pointSessionTokens[key];

      const { place, status } = await this._placeQueue.run(() => resolvePlace(
        this.$gmapApiPromiseLazy.bind(this), query,
        { fields: ['place_id', 'name', 'geometry', 'address_components', 'formatted_address'], sessionToken },
      ));
      if (this._lookupIsStale(generation)) return;

      if (!place) {
        if (status === 'OVER_QUERY_LIMIT') {
          // Do NOT spend the fallback query here: the first one was never
          // answered, so a second would only add load while Google is asking
          // for less.
          this._markLookupWaiting(key);
          this._scheduleThrottleRetry(
            key, () => this._tryResolvePointQuery(resultIndex, pointIndex, query, fallback),
          );
          this.preseedPointInput(resultIndex, pointIndex, query);
          return;
        }
        if (fallback) {
          // Still outstanding — the fallback query is the same lookup, retried
          // with a different phrasing.
          this._tryResolvePointQuery(resultIndex, pointIndex, fallback, '');
        } else {
          this._endLookup(key);
          this.preseedPointInput(resultIndex, pointIndex, query);
        }
        return;
      }

      this._endLookup(key);
      if (this._pointAlreadyPlaced(resultIndex, pointIndex)) return;

      await this.$gmapApiPromiseLazy();
      if (this._lookupIsStale(generation)) return;
      this.$set(this.pointSessionTokens, key, new google.maps.places.AutocompleteSessionToken()); // eslint-disable-line no-undef
      this.documentImportSetPointPlace({ resultIndex, pointIndex, place });
    },

    preseedPointInput(resultIndex, pointIndex, query) {
      this.$nextTick(() => {
        const ref = this.$refs[`pointInput-${resultIndex}-${pointIndex}`];
        if (!ref) return;
        const el = Array.isArray(ref) ? ref[0] : ref;
        const input = el && el.$el ? el.$el.querySelector('input') : null;
        if (!input || input.value) return;
        input.value = query;
      });
    },

    docTypeLabel(type) {
      const keyMap = {
        accommodation: 'importDocumentsTypeAccommodation',
        flight: 'importDocumentsTypeFlight',
        car_rental: 'importDocumentsTypeCarRental',
        attraction: 'importDocumentsTypeAttraction',
        show: 'importDocumentsTypeShow',
        itinerary: 'importDocumentsTypeItinerary',
      };
      return this.t(keyMap[type] || 'importDocumentsTypeUnknown');
    },

    friendlyCreationError(code) {
      const map = {
        date_are_not_in_range: this.t('importDocumentsErrorDateRange'),
      };
      return map[code] || code;
    },
  },
};
</script>

<style scoped>

.ride-documents-block {
  font-size: 1.4rem;
}

.ride-documents-panel__body {
  padding: 4px 0;
}

.ride-documents-panel__ai-notice {
  margin: 16px 0 4px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #f4f6f9;
  color: #666;
  font-size: 12px;
  line-height: 1.4;
}

.ride-documents-panel__lookup-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
  padding: 8px 12px;
  border-radius: 6px;
  background: #eef4fb;
  color: #2c5d8f;
  font-size: 13px;
}

.ride-documents-panel__lookup {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 6px;
  color: #2c5d8f;
  font-size: 12px;
}

.ride-documents-panel__lookup-spinner {
  flex: none;
  width: 14px;
  height: 14px;
  border: 2px solid #c8dcf0;
  border-top-color: #2c5d8f;
  border-radius: 50%;
  animation: ride-documents-panel-spin 0.8s linear infinite;
}

@keyframes ride-documents-panel-spin {
  to { transform: rotate(360deg); }
}

.ride-documents-panel__cards {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 16px;
}

.ride-documents-panel__card {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px;
}

.ride-documents-panel__card--error {
  border-color: #e44;
  background: #fff5f5;
}

.ride-documents-panel__card-processing {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ride-documents-panel__processing-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ride-documents-panel__processing-elapsed {
  font-size: 1.05em;
  font-weight: 600;
  color: #555;
}

.ride-documents-panel__processing-hint {
  font-size: 0.8em;
  color: #888;
}

.ride-documents-panel__spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #ccc;
  border-top-color: #555;
  border-radius: 50%;
  animation: ride-documents-panel-spin 0.8s linear infinite;
}

.ride-documents-panel__filename {
  color: #888;
  font-size: 0.85em;
}

.ride-documents-panel__warning {
  background: #fff8e1;
  padding: 6px 10px;
  border-radius: 4px;
  margin-bottom: 10px;
  font-size: 0.9em;
}

.ride-documents-panel__hotel-name {
  margin: 0 0 8px;
  font-size: 1.05em;
}

.ride-documents-panel__field {
  margin: 4px 0;
  font-size: 0.9em;
}

.ride-documents-panel__card-text {
  color: #555;
  font-size: 0.85em;
  margin-top: 8px;
  white-space: pre-line;
}

.ride-documents-panel__card-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.ride-documents-panel__input {
  display: block;
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-bottom: 8px;
  box-sizing: border-box;
  font-size: inherit;
}

.ride-documents-panel__input--date {
  width: auto;
  flex: 1;
}

.ride-documents-panel__textarea {
  display: block;
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  min-height: 70px;
  margin-bottom: 8px;
  box-sizing: border-box;
  resize: vertical;
  font-size: inherit;
}

.ride-documents-panel__date-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.ride-documents-panel__btn-edit,
.ride-documents-panel__btn-save {
  padding: 6px 14px;
  border: 1px solid #555;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 0.9em;
}

.ride-documents-panel__btn-discard {
  padding: 6px 14px;
  border: 1px solid #e44;
  color: #e44;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 0.9em;
}

.ride-documents-panel__btn-attach-one {
  padding: 6px 14px;
  border: none;
  border-radius: 4px;
  background: #2a7ae2;
  color: white;
  cursor: pointer;
  font-size: 0.9em;
}

.ride-documents-panel__btn-attach-one:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ride-documents-panel__doc-type-badge {
  display: inline-block;
  padding: 3px 10px;
  background: #e0f0ff;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: 600;
  color: #1a56a0;
  margin-bottom: 10px;
}

.ride-documents-panel__ready-chip {
  display: inline-block;
  padding: 3px 10px;
  background: #e3f8ea;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: 600;
  color: #157347;
  margin-bottom: 10px;
  margin-inline-start: 6px;
}

.ride-documents-panel__point {
  padding: 10px 0;
  border-top: 1px solid #eee;
}

.ride-documents-panel__point:first-of-type {
  border-top: none;
  padding-top: 0;
}

.ride-documents-panel__point-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  flex-wrap: wrap;
  gap: 4px;
}

.ride-documents-panel__btn-remove-point {
  padding: 2px 8px;
  border: 1px solid #e44;
  color: #e44;
  border-radius: 4px;
  background: white;
  font-size: 0.8em;
  cursor: pointer;
  margin-left: auto;
}

.ride-documents-panel__point-label {
  font-weight: 600;
  font-size: 0.9em;
}

.ride-documents-panel__point-date {
  font-size: 0.8em;
  color: #666;
}

.ride-documents-panel__point-search-query {
  font-size: 0.75em;
  color: #999;
  margin-top: 4px;
  font-style: italic;
}

.ride-documents-panel__location {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eee;
}

.ride-documents-panel__location-warning {
  font-size: 0.8em;
  color: #b45309;
  margin-bottom: 8px;
}

.ride-documents-panel__location-search ::v-deep input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #f59e0b;
  border-radius: 4px;
  font-size: 0.9em;
  box-sizing: border-box;
  direction: ltr;
  text-align: left;
}

.ride-documents-panel__location-confirmed {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 4px;
  flex-wrap: wrap;
}

.ride-documents-panel__location-check {
  color: #16a34a;
  font-size: 1.1em;
}

.ride-documents-panel__location-name {
  flex: 1;
  font-size: 0.9em;
  color: #166534;
}

.ride-documents-panel__btn-change-location {
  padding: 4px 10px;
  border: 1px solid #16a34a;
  color: #16a34a;
  border-radius: 4px;
  background: white;
  font-size: 0.85em;
  cursor: pointer;
}

.ride-documents-panel__missing-fields {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}

.ride-documents-panel__missing-label {
  font-size: 0.8em;
  color: #888;
}

.ride-documents-panel__missing-badge {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.78em;
  color: #856404;
}

.ride-documents-panel__partial-errors {
  border: 1px solid #e44;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 16px;
  background: #fff5f5;
}

.ride-documents-panel__partial-errors-title {
  margin: 0 0 8px;
  font-weight: bold;
  color: #c00;
  font-size: 0.9em;
}

.ride-documents-panel__error-list {
  margin: 0;
  padding-left: 20px;
}

.ride-documents-panel__error-item {
  font-size: 0.85em;
  color: #555;
}

.ride-documents-blocker {
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  background: #fffbeb;
}

.ride-documents-blocker--panel {
  margin-bottom: 16px;
}

.ride-documents-blocker__text {
  margin: 0 0 6px;
  font-size: 0.9em;
  color: #92400e;
  font-weight: 600;
}

.ride-documents-blocker__hint {
  margin: 0 0 8px;
  font-size: 0.8em;
  color: #a16207;
}

.ride-documents-blocker__date-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 0.85em;
}

.ride-documents-blocker__date-row input {
  margin-bottom: 0;
  direction: ltr;
}

.ride-documents-blocker__input--invalid {
  border-color: #dc2626;
  background: #fef2f2;
}

.ride-documents-blocker__error {
  margin: 6px 0 0;
  font-size: 0.8em;
  color: #b91c1c;
  font-weight: 600;
}

.ride-documents-blocker__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.ride-documents-blocker__actions button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ride-documents-panel__attach-hint {
  margin: 8px 0 0;
  font-size: 0.8em;
  color: #b45309;
}

.ride-documents-panel__pending-notice {
  margin: 0;
  padding: 8px 16px;
  flex-shrink: 0;
  text-align: center;
  font-size: 0.85em;
  color: #92400e;
  background: #fffbeb;
  border-top: 1px solid #f5e2b3;
}

.ride-documents-panel__success-notice {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  color: #166534;
  font-size: 0.9em;
  font-weight: 600;
  text-align: center;
}

.ride-documents-panel__attach-bar {
  padding: 12px 16px;
  border-top: 1px solid #e5e5e5;
  flex-shrink: 0;
  text-align: center;
}

.ride-documents-panel__btn-attach {
  width: 100%;
  padding: 12px 24px;
  background: #2a7ae2;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

.ride-documents-panel__btn-attach:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (prefers-color-scheme: dark) {
  .ride-documents-panel__ai-notice {
    background: #2a2a2a;
    color: #aaa;
  }
  .ride-documents-panel__card {
    border-color: #444;
    background: #222;
  }
  .ride-documents-panel__input,
  .ride-documents-panel__textarea,
  .ride-documents-panel__location-confirmed {
    background: #052e16;
    border-color: #166534;
  }
  .ride-documents-panel__location-name {
    color: #86efac;
  }
  .ride-documents-panel__ready-chip {
    background: #052e16;
    color: #86efac;
  }
  .ride-documents-panel__pending-notice {
    background: #2a2009;
    border-color: #a16207;
    color: #fcd34d;
  }
  .ride-documents-panel__success-notice {
    background: #052e16;
    border-color: #166534;
    color: #86efac;
  }
  .ride-documents-blocker {
    background: #2a2009;
    border-color: #a16207;
  }
  .ride-documents-blocker__text {
    color: #fcd34d;
  }
  .ride-documents-blocker__hint {
    color: #d6bb7a;
  }
  .ride-documents-blocker__error {
    color: #fca5a5;
  }
  .ride-documents-blocker__input--invalid {
    background: #3f1212;
    border-color: #dc2626;
  }
}

</style>
